import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import axios from 'axios';

interface School {
  id: string;
  name: string;
  code: string;
  email: string;
  isActive: boolean;
  _count?: {
    users: number;
    students: number;
    staff: number;
  };
}

/**
 * Schools List Page (SuperAdmin)
 * Lists all schools and provides management actions
 */
const SchoolsList: React.FC = () => {
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState<School | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSchools();
  }, []);

  // Refresh dashboard stats when schools change
  useEffect(() => {
    const eventListener = () => {
      // Trigger dashboard refresh if on dashboard page
      if (window.location.pathname === '/dashboard') {
        window.location.reload();
      }
    };
    window.addEventListener('schoolUpdated', eventListener);
    return () => window.removeEventListener('schoolUpdated', eventListener);
  }, []);

  const fetchSchools = async () => {
    try {
      const response = await axios.get('/schools');
      setSchools(response.data);
    } catch (error) {
      console.error('Failed to fetch schools:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSchool) return;

    try {
      await axios.delete(`/schools/${selectedSchool.id}`);
      setDeleteDialogOpen(false);
      setSelectedSchool(null);
      fetchSchools();
      // Trigger dashboard refresh
      window.dispatchEvent(new Event('schoolUpdated'));
    } catch (error: any) {
      console.error('Failed to delete school:', error);
      alert(error.response?.data?.message || 'Failed to delete school');
    }
  };

  const handleStatusToggle = async (school: School) => {
    try {
      await axios.patch(`/schools/${school.id}`, {
        isActive: !school.isActive,
      });
      setSchools(schools.map((s) => (s.id === school.id ? { ...s, isActive: !s.isActive } : s)));
      window.dispatchEvent(new Event('schoolUpdated'));
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update status');
    }
  };

  return (
    <MainLayout>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h4">Schools Management</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/superadmin/schools/create')}
          >
            Create School
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Users</TableCell>
                <TableCell>Students</TableCell>
                <TableCell>Staff</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : schools.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No schools found
                  </TableCell>
                </TableRow>
              ) : (
                schools.map((school) => (
                  <TableRow key={school.id}>
                    <TableCell>{school.name}</TableCell>
                    <TableCell>{school.code}</TableCell>
                    <TableCell>{school.email}</TableCell>
                    <TableCell>
                      <Switch
                        checked={school.isActive}
                        onChange={() => handleStatusToggle(school)}
                        color="success"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{school._count?.users || 0}</TableCell>
                    <TableCell>{school._count?.students || 0}</TableCell>
                    <TableCell>{school._count?.staff || 0}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/superadmin/schools/${school.id}`)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/superadmin/schools/${school.id}/edit`)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedSchool(school);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete School</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to permanently delete {selectedSchool?.name}? This action cannot be undone and will delete all related data.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </MainLayout>
  );
};

export default SchoolsList;

